import { type TaskCardResource } from "@wayflyer/flyui";
import {
  IconChatLines16Line,
  IconCheckCircle16Line,
  IconConnections16Line,
  IconEmptyPage16Line,
  IconShop16Line,
} from "@wayflyer/flyui-icons/16/line";
import { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import { type IntlShape } from "react-intl";
import { getConnectorNameForFE, getPlatformStringFromConnectionCategory } from "shared-ui";

import { MESSAGES } from "./message-definitions";

const getConnectionResources = (
  intl: IntlShape,
  connectionMetadata: FundingApplicationSchemas.ConnectionMetadataSchema[]
): TaskCardResource[] =>
  connectionMetadata.map((meta) => ({
    icon: IconConnections16Line,
    label: meta.identifier
      ? intl.formatMessage(MESSAGES.connectedPlatformWithIdentifier, {
          connectorName: getConnectorNameForFE(meta.connectorName),
          connectorIdentifier: meta.identifier,
        })
      : intl.formatMessage(MESSAGES.connectedPlatformWithoutIdentifier, {
          connectorName: getConnectorNameForFE(meta.connectorName),
        }),
  }));

const REVENUE_SOURCE_REASON_MESSAGES: Partial<
  Record<FundingApplicationSchemas.NoOtherConnectionsReasonCode, { id: string; defaultMessage: string }>
> = {
  company_less_than_6m_old: MESSAGES.responseReasonLessThan180Days,
  store_decommissioned: MESSAGES.responseReasonPreviousCompanyDecommissioned,
  no_other_revenue_sources: MESSAGES.responseReasonNoOtherPlatforms,
};

const getRevenueSourceResources = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): TaskCardResource[] => {
  const response = air.response as FundingApplicationSchemas.AdditionalRevenueSourceAIRResponse;
  if (response.connection_ids.length > 0) {
    return getConnectionResources(intl, air.meta as FundingApplicationSchemas.ConnectionMetadataSchema[]);
  }
  if (response.no_other_connections_reason === "unsupported_store" && response.unsupported_source_name) {
    return [{ icon: IconShop16Line, label: response.unsupported_source_name }];
  }
  if (response.no_other_connections_reason === "other" && response.reason) {
    return [{ icon: IconCheckCircle16Line, label: response.reason }];
  }
  if (response.no_other_connections_reason) {
    const message = REVENUE_SOURCE_REASON_MESSAGES[response.no_other_connections_reason];
    return message ? [{ icon: IconChatLines16Line, label: intl.formatMessage(message) }] : [];
  }
  return [];
};

const getReconnectConnectionResources = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): TaskCardResource[] => {
  const airData = air.data as FundingApplicationSchemas.ReconnectConnectionAIRSchema;
  const response = air.response as FundingApplicationSchemas.ReconnectConnectionAIRResponse;
  if (response.reconnected_connection_id) {
    return getConnectionResources(intl, air.meta as FundingApplicationSchemas.ConnectionMetadataSchema[]);
  }
  if (response.cannot_reconnect_connection_reason === "other" && response.reason) {
    return [{ icon: IconChatLines16Line, label: response.reason }];
  }
  if (response.cannot_reconnect_connection_reason === "store_decommissioned") {
    return [
      {
        icon: IconChatLines16Line,
        label: intl.formatMessage(MESSAGES.responseReasonPlatformDecommissioned, {
          platformType: getPlatformStringFromConnectionCategory(
            intl,
            airData.connection_meta_data?.type,
            airData.connection_identifier ?? ""
          ),
        }),
      },
    ];
  }
  if (response.cannot_reconnect_connection_reason === "bank_statements_provided") {
    return [{ icon: IconEmptyPage16Line, label: intl.formatMessage(MESSAGES.responseBankStatementsProvided) }];
  }
  if (response.cannot_reconnect_connection_reason === "bank_accounts_closed" && response.bank_account_close_dates) {
    return response.bank_account_close_dates.map(({ account_mask, close_date }) => ({
      icon: IconCheckCircle16Line,
      label: account_mask
        ? intl.formatMessage(MESSAGES.responseBankAccountClosedWithMask, {
            accountMask: account_mask,
            closeDate: dayjs(close_date).format("LL"),
          })
        : intl.formatMessage(MESSAGES.responseBankAccountClosedNoMask, {
            closeDate: dayjs(close_date).format("LL"),
          }),
    }));
  }
  return [];
};

export const getCompletedAirResources = (
  intl: IntlShape,
  air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
): TaskCardResource[] => {
  if (air.status === "superseded") {
    return [{ icon: IconCheckCircle16Line, label: intl.formatMessage(MESSAGES.responseSupersededLabel) }];
  }
  if (!air.response) {
    return [];
  }
  switch (air.type) {
    case "bank_statements_for_account": {
      const filenames = air.meta as string[];
      const response = air.response as FundingApplicationSchemas.BankStatementsForAccountAIRResponse;
      const resources: TaskCardResource[] = filenames.map((filename) => ({
        icon: IconEmptyPage16Line,
        label: filename,
      }));
      // Customer can leave a free-text reason alongside (or instead of) uploaded statements —
      // include it so partial uploads with context are fully captured.
      if (response.reason) {
        resources.push({ icon: IconChatLines16Line, label: response.reason });
      }
      return resources;
    }
    case "additional_revenue_source":
      return getRevenueSourceResources(intl, air);
    case "reconnect_connection":
      return getReconnectConnectionResources(intl, air);
    default:
      return [];
  }
};
